import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Crosshair, PlusCircle, Search } from 'lucide-react';
import { PageSEO } from '../../components/seo/SchemaMarkup';
import { API, Eyebrow, LocatorLoading, StatBar, therapyLabel, useLocatorPageView } from './locatorShared';

export default function LocatorHub() {
  useLocatorPageView();
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/api/locator`).then((r) => r.json()).then(setData).catch(() => {});
  }, []);

  const results = useMemo(() => {
    if (!data || q.trim().length < 2) return [];
    const needle = q.trim().toLowerCase();
    const metros = data.metros.filter((m) => m.name.toLowerCase().includes(needle)).map((m) => ({ type: 'metro', label: `${m.name} — ${m.count} facilities`, url: `/locator/${m.slug}` }));
    const therapies = data.therapies.filter((t) => t.has_hub && t.name.toLowerCase().includes(needle)).map((t) => ({ type: 'therapy', label: `${t.name} — ${t.count} nationwide`, url: `/locator/therapy/${t.slug}` }));
    const facs = data.search.filter((f) => f.name.toLowerCase().includes(needle)).slice(0, 6).map((f) => ({ type: 'facility', label: `${f.name} · ${f.city}, ${f.state}`, url: f.page ? `/locator/${f.page}#${f.slug}` : `/locator/claim/${f.slug}` }));
    return [...metros, ...therapies, ...facs].slice(0, 10);
  }, [data, q]);

  if (!data) return <LocatorLoading />;

  return (
    <div className="bg-black min-h-screen px-6 pt-28 pb-16" data-testid="locator-hub">
      <PageSEO
        title={`Recovery & Peptide Therapy Locator — ${data.total_facilities}+ Vetted Facilities | NinjAthlete`}
        description={`Find cold plunge, infrared sauna, red light, IV and peptide therapy near you. ${data.total_facilities} vetted recovery clinics across ${data.metros.length} metros, browsable by city and therapy.`}
        path="/locator"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Recovery Locator', url: '/locator' }]}
      />
      <div className="max-w-5xl mx-auto">
        <Eyebrow>Recovery &amp; Peptide Therapy · Nationwide · Updated August 2026</Eyebrow>
        <h1 className="text-white text-4xl sm:text-5xl lg:text-6xl leading-tight" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="locator-hub-h1">
          Find Recovery &amp; Peptide Therapy Near You
        </h1>
        <p className="text-zinc-400 text-base leading-relaxed mt-4 max-w-2xl">
          <strong className="text-white">{data.total_facilities} vetted recovery clinics, studios and biohacking labs</strong> — cold plunge, infrared sauna, red light, IV drips, peptide therapy and more. Browse by city, by therapy, or use your location.
        </p>

        <div className="relative mt-8 max-w-xl">
          <div className="flex items-center border border-zinc-700 focus-within:border-[#C7FA3E] bg-zinc-900 transition-colors">
            <Search className="w-4 h-4 text-zinc-500 ml-4" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search a city, therapy or facility…"
              className="w-full bg-transparent outline-none text-white text-sm px-3 py-3.5 placeholder:text-zinc-600"
              data-testid="locator-search-input"
            />
          </div>
          {results.length > 0 && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-zinc-900 border border-zinc-700 max-h-80 overflow-y-auto" data-testid="locator-search-results">
              {results.map((r) => (
                <Link key={r.url + r.label} to={r.url} className="block px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-800 hover:text-[#C7FA3E] border-b border-zinc-800/60 last:border-0">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-600 mr-2">{r.type}</span>{r.label}
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-3 mt-5">
          <Link to="/locator/near-me" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#C7FA3E] text-black text-xs font-bold uppercase tracking-widest hover:opacity-90 transition-opacity" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid="locator-near-me-btn">
            <Crosshair className="w-3.5 h-3.5" /> Use My Location
          </Link>
          <Link to="/locator/submit" className="inline-flex items-center gap-2 px-4 py-2.5 border border-zinc-700 text-zinc-300 text-xs uppercase tracking-widest hover:border-[#C7FA3E] hover:text-[#C7FA3E] transition-colors" data-testid="locator-submit-btn">
            <PlusCircle className="w-3.5 h-3.5" /> Add Your Facility — Free
          </Link>
        </div>

        <StatBar
          testPrefix="locator-hub"
          stats={[
            [`${data.total_facilities}`, 'Vetted facilities'],
            [`${data.metros.length}`, 'Metro directories'],
            [`${data.therapies.filter((t) => t.has_hub).length}`, 'Therapy guides'],
            [`${data.states.length}`, 'State directories'],
          ]}
        />

        <section className="mt-10" data-testid="locator-hub-metros">
          <h2 className="text-white text-3xl mb-5" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Browse by City</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.metros.map((m) => (
              <Link key={m.slug} to={`/locator/${m.slug}`} className="border border-zinc-800 hover:border-[#C7FA3E]/40 bg-zinc-900/60 p-4 transition-colors group" data-testid={`hub-metro-${m.slug}`}>
                <div className="flex items-center justify-between">
                  <p className="text-white group-hover:text-[#C7FA3E] transition-colors" style={{ fontFamily: 'Oswald, sans-serif' }}>{m.name}</p>
                  <ArrowRight className="w-3.5 h-3.5 text-zinc-600 group-hover:text-[#C7FA3E] transition-colors" />
                </div>
                <p className="text-zinc-500 text-xs mt-1">{m.count} facilities{m.top_therapies?.length ? ` · ${m.top_therapies.join(' · ')}` : ''}</p>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-12" data-testid="locator-hub-therapies">
          <h2 className="text-white text-3xl mb-5" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Browse by Therapy</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.therapies.filter((t) => t.has_hub).map((t) => (
              <Link key={t.slug} to={`/locator/therapy/${t.slug}`} className="border border-zinc-800 hover:border-[#C7FA3E]/40 bg-zinc-900/60 p-4 transition-colors group" data-testid={`hub-therapy-${t.slug}`}>
                <p className="text-white group-hover:text-[#C7FA3E] transition-colors" style={{ fontFamily: 'Oswald, sans-serif' }}>{t.name}</p>
                <p className="text-zinc-500 text-xs mt-1">{t.count} facilities nationwide</p>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-12" data-testid="locator-hub-states">
          <h2 className="text-white text-3xl mb-5" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Browse by State</h2>
          <div className="flex flex-wrap gap-2">
            {data.states.map((s) => (
              <Link key={s.slug} to={`/locator/${s.slug}`} className="px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:border-[#C7FA3E] hover:text-[#C7FA3E] text-xs uppercase tracking-wider transition-colors" data-testid={`hub-state-${s.slug}`}>
                {s.name} ({s.count})
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-14 border border-zinc-800 bg-zinc-900/60 p-6">
          <h2 className="text-white text-2xl mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Run a Recovery Facility?</h2>
          <p className="text-zinc-400 text-sm mb-4 max-w-xl">Get listed free in front of an audience already reading about cold plunge, peptides and recovery every day. Claimed listings add photos, pricing, staff and a tracked booking link.</p>
          <Link to="/locator/submit" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#C7FA3E] text-black text-xs font-bold uppercase tracking-widest hover:opacity-90 transition-opacity" style={{ fontFamily: 'Oswald, sans-serif' }}>
            Add Your Facility <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </section>
      </div>
    </div>
  );
}
